import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { cloudService } from '../services/cloudService';
import apiClient from '../services/apiClient';
import CloudExecutiveLayout from './cloudExecutive/CloudExecutiveLayout';
import { createAwsPresentation } from './cloudExecutive/awsPresentation';
import { createAzurePresentation } from './cloudExecutive/azurePresentation';
import { createGcpPresentation } from './cloudExecutive/gcpPresentation';
import { ExecutivePresentation, Provider } from './cloudExecutive/types';
import './CloudExecutiveDashboard.css';

interface CloudExecutiveDashboardProps {
  provider: Provider;
}

const cloudAccountIdForAzureSubscription = (subscriptionId: string) =>
  `azure_${subscriptionId}`.replace(/[^a-zA-Z0-9_-]/g, '_');

const hasMeaningfulAwsDashboardData = (data: any) => {
  const inventory = data?.inventory || {};
  const inventoryTotal = Object.values(inventory).reduce<number>((sum, value) => sum + Number(value || 0), 0);
  const detailTotal = [
    data?.ec2Details,
    data?.rdsDetails,
    data?.s3Details,
    data?.eksDetails,
    data?.idleResourcesDetails,
    data?.securityDetails,
    data?.topServices
  ].reduce((sum, value) => sum + (Array.isArray(value) ? value.length : 0), 0);

  return Boolean(
    Number(data?.overview?.monthlyAverageCost || 0) > 0 ||
    Number(data?.overview?.netCost || 0) > 0 ||
    inventoryTotal > 0 ||
    detailTotal > 0
  );
};

const CloudExecutiveDashboard: React.FC<CloudExecutiveDashboardProps> = ({ provider }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const reportIdFromUrl = urlParams.get('reportId') || '';
  const cloudAccountIdFromUrl = urlParams.get('cloudAccountId') || '';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any | null>(null);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [snapshotMode, setSnapshotMode] = useState(false);

  const loadReportSnapshot = useCallback(() => {
    const rawSnapshot = localStorage.getItem('reportSnapshot');
    if (!rawSnapshot) return false;

    try {
      const parsed = JSON.parse(rawSnapshot);
      if (parsed?.provider !== provider || !parsed?.data) return false;

      const snapshotData = {
        ...parsed.data,
        reportId: parsed.data?.reportId || parsed.reportId
      };
      setSnapshotMode(true);
      setData(snapshotData);
      if (provider === 'azure' && snapshotData.subscription?.id) {
        setSubscriptions([snapshotData.subscription]);
        setSelectedSubscription(snapshotData.subscription.id);
      }
      localStorage.removeItem('reportSnapshot');
      setLoading(false);
      setError(null);
      return true;
    } catch (snapshotError) {
      console.error('Erro ao carregar snapshot do relatório:', snapshotError);
      localStorage.removeItem('reportSnapshot');
      return false;
    }
  }, [provider]);

  const loadAzureSubscriptions = useCallback(async () => {
    if (loadReportSnapshot()) return;

    try {
      setSnapshotMode(false);
      setLoading(true);
      setError(null);
      const response = await cloudService.getAzureSubscriptions();
      const allSubscriptions = Array.isArray(response) ? response : [];
      const selectedSubscriptions = (() => {
        try {
          return JSON.parse(localStorage.getItem('azureSelectedSubscriptions') || '[]');
        } catch (error) {
          return [];
        }
      })();
      const nextSubscriptions = selectedSubscriptions.length
        ? allSubscriptions.filter(subscription => selectedSubscriptions.includes(subscription.id))
        : allSubscriptions;
      setSubscriptions(nextSubscriptions);

      if (nextSubscriptions.length > 0) {
        const savedSubscription = localStorage.getItem('selectedSubscription');
        const hasSavedSubscription = nextSubscriptions.some(subscription => subscription.id === savedSubscription);
        setSelectedSubscription(prev => prev || (hasSavedSubscription ? savedSubscription || '' : nextSubscriptions[0].id));
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Erro ao carregar subscriptions Azure:', err);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Azure nao autenticado. Conecte sua conta em "Conectar Clouds" para carregar subscriptions reais.');
      } else {
        setError('Nao foi possivel carregar as subscriptions Azure.');
      }
      setLoading(false);
    }
  }, [loadReportSnapshot]);

  const loadAzureData = useCallback(async (subscriptionId: string) => {
    if (snapshotMode) return;

    try {
      setLoading(true);
      setError(null);
      setData(null);
      const selectedSubscriptionData = subscriptions.find(subscription => subscription.id === subscriptionId);
      const shell = await cloudService.getAzureFinOpsShell(subscriptionId);

      setData({
        ...shell,
        cloudAccountId: shell?.cloudAccountId || cloudAccountIdForAzureSubscription(subscriptionId),
        subscription: {
          id: subscriptionId,
          name: selectedSubscriptionData?.name || shell?.subscription?.name || 'Subscription selecionada'
        }
      });
      setLoading(false);

      const mergeBlock = (partial: any) => {
        setData((current: any) => ({
          ...(current || {}),
          ...(partial || {}),
          executiveSummary: {
            ...(current?.executiveSummary || {}),
            ...(partial?.executiveSummary || {}),
            idleResources: {
              ...(current?.executiveSummary?.idleResources || {}),
              ...(partial?.executiveSummary?.idleResources || {})
            },
            securityAlerts: {
              ...(current?.executiveSummary?.securityAlerts || {}),
              ...(partial?.executiveSummary?.securityAlerts || {})
            }
          },
          inventory: {
            ...(current?.inventory || {}),
            ...(partial?.inventory || {})
          },
          subscription: {
            id: subscriptionId,
            name: selectedSubscriptionData?.name || current?.subscription?.name || shell?.subscription?.name || 'Subscription selecionada'
          },
          cloudAccountId: current?.cloudAccountId || shell?.cloudAccountId || cloudAccountIdForAzureSubscription(subscriptionId)
        }));
      };

      const blocks = ['services', 'inventory', 'security', 'rightsizing'];
      blocks.forEach(async (block) => {
        try {
          const blockResponse = await cloudService.getAzureFinOpsBlock(subscriptionId, block);
          mergeBlock(blockResponse);
        } catch (blockError) {
          console.error(`Erro ao carregar bloco Azure ${block}:`, blockError);
        }
      });
    } catch (err) {
      console.error('Erro ao carregar dashboard Azure:', err);
      setData(null);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Azure nao autenticado. Conecte sua conta em "Conectar Clouds" para visualizar o dashboard Azure.');
      } else {
        setError('Nao foi possivel carregar os dados executivos do Azure.');
      }
      setLoading(false);
    }
  }, [subscriptions, snapshotMode]);

  const loadAwsData = useCallback(async () => {
    if (reportIdFromUrl) {
      try {
        setSnapshotMode(true);
        setLoading(true);
        setError(null);
        const response = await apiClient.get(`/reports/runs/${encodeURIComponent(reportIdFromUrl)}`, {
          params: { includeInventory: false }
        });
        const report = response.data?.report;
        if (!report?.snapshot) {
          setData(null);
          setError('O relatório informado não possui snapshot armazenado.');
          return;
        }
        const snapshotData = {
          ...report.snapshot,
          provider: 'aws',
          reportId: report.id || reportIdFromUrl,
          cloudAccountId: report.cloudAccountId || report.snapshot.cloudAccountId || cloudAccountIdFromUrl
        };
        if (snapshotData.cloudAccountId) {
          localStorage.setItem('awsCloudAccountId', snapshotData.cloudAccountId);
        }
        localStorage.setItem('awsData', JSON.stringify(snapshotData));
        setData(snapshotData);
        return;
      } catch (snapshotError) {
        console.error('Erro ao carregar relatório AWS pela URL:', snapshotError);
        setData(null);
        setError('Não foi possível carregar o relatório informado na URL.');
        return;
      } finally {
        setLoading(false);
      }
    }

    if (loadReportSnapshot()) return;

    const raw = localStorage.getItem('awsData');
    const cloudAccountId = cloudAccountIdFromUrl || localStorage.getItem('awsCloudAccountId');

    try {
      setSnapshotMode(false);
      setLoading(true);
      setError(null);

      if (cloudAccountId) {
        const response = await apiClient.get('/cloud/aws/finops-data', {
          params: { cloudAccountId },
          timeout: 120000
        });
        const nextData = {
          ...(response.data || {}),
          cloudAccountId
        };
        if (!hasMeaningfulAwsDashboardData(nextData)) {
          if (raw) {
            const parsed = JSON.parse(raw);
            setData({
              ...parsed,
              cloudAccountId: parsed.cloudAccountId || cloudAccountId || undefined
            });
            setError('A atualização da AWS retornou vazia. Exibindo os ultimos dados salvos localmente.');
            return;
          }

          setData(null);
          setError('A atualização da AWS retornou sem custos, inventário ou recomendações.');
          return;
        }
        localStorage.setItem('awsData', JSON.stringify(nextData));
        localStorage.setItem('awsCloudAccountId', cloudAccountId);
        setData(nextData);
        const nextReportId = String(nextData.reportId || '');
        const nextUrl = `/aws-dashboard?cloudAccountId=${encodeURIComponent(cloudAccountId)}` +
          (nextReportId ? `&reportId=${encodeURIComponent(nextReportId)}` : '');
        if (location.pathname + location.search !== nextUrl) {
          navigate(nextUrl, { replace: true });
        }
        return;
      }

      if (!raw) {
        setData(null);
        setError('Nenhuma conta AWS conectada no fluxo atual.');
      } else {
        const parsed = JSON.parse(raw);
        setData({
          ...parsed,
          cloudAccountId: parsed.cloudAccountId || localStorage.getItem('awsCloudAccountId') || undefined
        });
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard AWS:', err);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          setData({
            ...parsed,
            cloudAccountId: parsed.cloudAccountId || cloudAccountId || undefined
          });
          setError('Nao foi possivel atualizar a AWS agora. Exibindo os ultimos dados salvos localmente.');
          return;
        } catch (parseError) {
          console.error('Erro ao carregar cache AWS:', parseError);
        }
      }
      setData(null);
      setError('Nao foi possivel carregar os dados AWS conectados.');
    } finally {
      setLoading(false);
    }
  }, [
    cloudAccountIdFromUrl,
    loadReportSnapshot,
    location.pathname,
    location.search,
    navigate,
    reportIdFromUrl
  ]);

  const loadGcpData = useCallback(() => {
    if (loadReportSnapshot()) return;

    try {
      setSnapshotMode(false);
      setLoading(true);
      setError(null);
      const raw = localStorage.getItem('gcpData');
      if (!raw) {
        setData(null);
        setError('Nenhum relatório GCP carregado no fluxo atual. Atualize o projeto em "Contas e Relatórios".');
      } else {
        const parsed = JSON.parse(raw);
        setData({
          ...parsed,
          cloudAccountId: parsed.cloudAccountId || localStorage.getItem('gcpCloudAccountId') || undefined
        });
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard GCP:', err);
      setError('Nao foi possivel carregar os dados GCP salvos localmente.');
    } finally {
      setLoading(false);
    }
  }, [loadReportSnapshot]);

  useEffect(() => {
    if (provider === 'azure') {
      loadAzureSubscriptions();
      return;
    }

    if (provider === 'gcp') {
      if (!snapshotMode) {
        loadGcpData();
      }

      const handleAutoRefresh = () => {
        if (!snapshotMode) loadGcpData();
      };
      const handleStorage = () => {
        if (!snapshotMode) loadGcpData();
      };
      window.addEventListener('autoRefresh', handleAutoRefresh);
      window.addEventListener('storage', handleStorage);
      return () => {
        window.removeEventListener('autoRefresh', handleAutoRefresh);
        window.removeEventListener('storage', handleStorage);
      };
    }

    if (!snapshotMode) {
      loadAwsData();
    }

    const handleAutoRefresh = () => {
      if (!snapshotMode) loadAwsData();
    };
    const handleStorage = () => {
      if (!snapshotMode) loadAwsData();
    };
    window.addEventListener('autoRefresh', handleAutoRefresh);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('autoRefresh', handleAutoRefresh);
      window.removeEventListener('storage', handleStorage);
    };
  }, [provider, loadAzureSubscriptions, loadAwsData, loadGcpData, snapshotMode]);

  useEffect(() => {
    if (provider === 'azure' && selectedSubscription && !snapshotMode) {
      localStorage.setItem('selectedSubscription', selectedSubscription);
      loadAzureData(selectedSubscription);
    }
  }, [provider, selectedSubscription, loadAzureData, snapshotMode]);

  const presentation = useMemo<ExecutivePresentation | null>(() => {
    if (!data) return null;
    if (provider === 'azure') return createAzurePresentation(data);
    if (provider === 'gcp') return createGcpPresentation(data);
    return createAwsPresentation(data);
  }, [provider, data]);

  if (loading) {
    return (
      <div className="cloud-exec">
        <div className="cloud-exec__panel">
          <div className="cloud-exec__empty">
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>Carregando dados executivos...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!presentation) {
    return (
      <div className="cloud-exec">
        <div className="cloud-exec__panel">
          <div className="cloud-exec__empty">{error || 'Nenhum dado disponivel para este provider.'}</div>
        </div>
      </div>
    );
  }

  return (
    <CloudExecutiveLayout
      presentation={presentation}
      error={error}
      selectorOptions={
        provider === 'azure'
          ? subscriptions.map(subscription => ({ label: subscription.name, value: subscription.id }))
          : []
      }
      selectedValue={provider === 'azure' ? selectedSubscription : undefined}
      onSelectChange={provider === 'azure' ? setSelectedSubscription : undefined}
    />
  );
};

export default CloudExecutiveDashboard;
